import { ReactNode, useState } from 'react'
import PageShell from '@/components/Common/PageShell'
import { DateRangeAccordion } from '@/components/DateRangeAccordion'
import DriveStatisticsTable from '@/components/DriveStatisticsTable'
import { requirePermission } from '@/utils/auth'
import { parseAndValidateDates } from '@/utils/datetime'
import { formatDateForTimestamp, formatDateForUrl } from '@/utils/datetime'
import { Button, Text } from '@mantine/core'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { TbRefresh } from 'react-icons/tb'
import dayjs from 'dayjs'
import { getDetailedDriveStatisticsOptions } from '@/api/main'
import { useSuspenseQuery } from '@tanstack/react-query'
import ErrorState from '@/components/Common/ErrorState'
import { QueryBoundary } from '@/components/Common/QueryBoundary'
import { useTranslation } from 'react-i18next'
import i18next from 'i18next'

export const Route = createFileRoute('/drive-statistics/$startDate/$endDate')({
  component: () => (
    <QueryBoundary
      loadingFallback={<DriveLoadingFallback />}
      errorTitle={i18next.t('failedToLoadDriveStatistics', { ns: 'statistics' })}
      errorMessage={i18next.t('unableToLoad', { ns: 'statistics' })}
      pageTitle={i18next.t('driveStatistics', { ns: 'statistics' })}
      fullWidth
      backButtonHref={ROUTE_PATH}
    >
      <RouteComponent />
    </QueryBoundary>
  ),
  beforeLoad: () => requirePermission('drive_statistics'),
  errorComponent: ({ error }) => (
    <PageShell title={i18next.t('errorLoadingDriveStatistics', { ns: 'statistics' })} fullWidth>
      <ErrorState
        title={i18next.t('failedToLoadDriveStatistics', { ns: 'statistics' })}
        message={error.message || i18next.t('unableToLoad', { ns: 'statistics' })}
      />
    </PageShell>
  ),
})

const ROUTE_PATH = '/drive-statistics/$startDate/$endDate'

function DriveStatisticsLayout({
  children,
  startDate,
  endDate,
  onRangeSelectionStateChange,
  onRefresh,
  isRefreshing,
}: {
  children: ReactNode
  startDate: Date
  endDate: Date
  onRangeSelectionStateChange?: (isRangeSelectionInProgress: boolean) => void
  onRefresh?: () => void
  isRefreshing: boolean
}) {
  const navigate = useNavigate()
  const { t } = useTranslation(['statistics', 'common'])

  const navigateToDateRange = (newStartDate: Date, newEndDate: Date) => {
    navigate({
      to: ROUTE_PATH,
      params: {
        startDate: formatDateForUrl(newStartDate),
        endDate: formatDateForUrl(newEndDate),
      },
    })
  }

  return (
    <PageShell
      title={t('driveStatistics')}
      fullWidth
      action={
        <Button
          variant="light"
          leftSection={<TbRefresh size={16} />}
          onClick={onRefresh}
          loading={isRefreshing}
          disabled={!onRefresh}
        >
          {t('refresh', { ns: 'common' })}
        </Button>
      }
    >
      <DateRangeAccordion
        startDate={startDate}
        endDate={endDate}
        onRangeChange={navigateToDateRange}
        onRangeSelectionStateChange={onRangeSelectionStateChange}
      />
      {children}
    </PageShell>
  )
}

function RouteComponent() {
  const { t } = useTranslation(['statistics', 'common'])
  const [isRangeSelectionInProgress, setIsRangeSelectionInProgress] = useState(false)

  const { startDate: startDateParam, endDate: endDateParam } = Route.useParams()
  const { startDate, endDate } = parseAndValidateDates(startDateParam, endDateParam)

  const {
    data: driveStatistics,
    refetch,
    isFetching,
  } = useSuspenseQuery(
    getDetailedDriveStatisticsOptions({
      path: {
        timestamp_from: formatDateForTimestamp(startDate),
        timestamp_to: formatDateForTimestamp(dayjs(endDate).add(1, 'day').toDate()),
      },
    })
  )

  return (
    <DriveStatisticsLayout
      startDate={startDate}
      endDate={endDate}
      onRangeSelectionStateChange={setIsRangeSelectionInProgress}
      onRefresh={() => void refetch()}
      isRefreshing={isFetching}
    >
      {isRangeSelectionInProgress ? (
        <Text ta="center" c="dimmed" py="xl" fz="xl" fw={600}>
          {t('selectRangeToSeeData', { ns: 'common' })}
        </Text>
      ) : (
        <DriveStatisticsTable data={driveStatistics || []} isLoading={false} error={null} />
      )}
    </DriveStatisticsLayout>
  )
}

function DriveLoadingFallback() {
  const { startDate: startDateParam, endDate: endDateParam } = Route.useParams()
  const { startDate, endDate } = parseAndValidateDates(startDateParam, endDateParam)

  return (
    <DriveStatisticsLayout startDate={startDate} endDate={endDate} isRefreshing>
      <DriveStatisticsTable data={[]} isLoading={true} error={null} />
    </DriveStatisticsLayout>
  )
}
